import { AfterViewInit, ChangeDetectorRef, Component, ErrorHandler, Inject, Injector, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, inject, signal, WritableSignal } from '@angular/core'
import { MatCheckbox } from '@angular/material/checkbox'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { AsiakkaanKuukausiruutuStatusSaveRequest, AsiakkaanKuukausiruutuStatusSaveResponse, KirjanpitoKuukausiruudunTilinpaatoksenTila, ListausAsiakas } from 'app/_jaettu-lemonator/model/asiakas'

import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { disabled, FieldTree, form, metadata, REQUIRED, required } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { firstValueFrom, Observable, of, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { KuukausiruudunTiedot } from './asiakkaat.datasource.service'

export interface AsiakkaanKirjanpitostatusKuukaudelleDialogData {
  asiakas: ListausAsiakas
  kuukausi: KuukausiruudunTiedot
}

interface KirjanpitostatusLomake {
  aktiivinen: boolean
  alvIlmoitusHoidettuJarjestelmanUlkopuolella: boolean
  tiedotOsinkojenMaksamisesta: KuukausiruudunTiedot['tom'] | null
  kirjanpitoTehty: boolean
  kirjanpitoPvm: Date | null
  kirjanpitoAika: string
  tilinpaatosTehty: boolean
  tilinpaatosPvm: Date | null
  veroilmoitusTehty: boolean
  veroilmoitusPvm: Date | null
  poytakirjaTehty: boolean
  poytakirjaPvm: Date | null
  viesti: string
}

type PoistettavaKentta = Exclude<keyof KirjanpitostatusLomake, 'aktiivinen' | 'kirjanpitoAika' | 'viesti'>

@Component({
  templateUrl: './asiakkaan-kirjanpitostatus-kuukaudelle.dialog.html',
  styleUrls: ['./asiakkaan-kirjanpitostatus-kuukaudelle.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanKirjanpitostatusKuukaudelleDialog implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('kirjanpitoaikaInput', { read: MatInput, static: true }) kirjanpitoaikaInput: MatInput
  @ViewChild('kirjanpitoTehtyCheckbox', { read: MatCheckbox, static: true }) kirjanpitoTehtyCheckbox: MatCheckbox

  // @ViewChild('tilinpaatosaikaInput', { read: MatInput }) tilinpaatosaikaInput: MatInput
  @ViewChild('tilinpaatosTehtyCheckbox', { read: MatCheckbox }) tilinpaatosTehtyCheckbox: MatCheckbox

  // @ViewChild('veroilmoitusaikaInput', { read: MatInput }) veroilmoitusaikaInput: MatInput
  @ViewChild('veroilmoitusTehtyCheckbox', { read: MatCheckbox }) veroilmoitusTehtyCheckbox: MatCheckbox

  // @ViewChild('poytakirjaaikaInput', { read: MatInput }) poytakirjaaikaInput: MatInput
  @ViewChild('poytakirjaPvmInput', { read: MatInput }) poytakirjaPvmInput: MatInput
  @ViewChild('poytakirjaTehtyCheckbox', { read: MatCheckbox }) poytakirjaTehtyCheckbox: MatCheckbox

  @ViewChild('aktiivinenCheckbox', { read: MatCheckbox, static: true }) aktiivinenCheckbox: MatCheckbox

  @ViewChild('muistiinpanotTextarea', { read: MatInput, static: true }) muistiinpanotTextarea: MatInput

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private onkoTilikaudenLoppu = false
  private onkoMurrettuTilikausi = false
  private onkoTmi = false

  private readonly _injector = inject(Injector)
  private readonly _poissa: Record<PoistettavaKentta, WritableSignal<boolean>>
  private readonly _pakollinen = {
    kirjanpitoPvm: signal(false),
    tilinpaatosPvm: signal(false),
    veroilmoitusPvm: signal(false),
    poytakirjaPvm: signal(false),
  }

  lomake: FieldTree<KirjanpitostatusLomake>
  readonly kirjanpitoAikaVahintaan = 1
  readonly kirjanpitoAikaEnintaan = 1440
  // Signal Formsissa käytöstä poistettu kenttä ei ole muokattu (dirty), joten käyttäjän muutos seurataan itse
  poytakirjaTehtyMuokattu = false

  lokalisoituKuukausiVuosi = ''
  asiakas: ListausAsiakas = null
  kuukausi: KuukausiruudunTiedot = null
  hinta: number = 0
  teho: string = ''
  naytaTilinpaatos = false
  naytaVeroilmoitus = false
  naytaPoytakirja = false

  kuukausiruutuUriObservable: Observable<string>
  kuukausiruutuEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>

  private _getKuukausiruutuUri(d: AsiakkaanKirjanpitostatusKuukaudelleDialogData): string {
    if (d?.asiakas?.k && d?.kuukausi?.k) {
      const lm = this._dateService.numberToLocalMonth(d.kuukausi.k)
      return this._asiakasUriService.annaListausRuutuUri(d.asiakas.k, lm)
    }
    return ''
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: AsiakkaanKirjanpitostatusKuukaudelleDialogData,
    private _dialogRef: MatDialogRef<AsiakkaanKirjanpitostatusKuukaudelleDialog>,
    private _errorHandler: ErrorHandler,
    private _changeDetectorRef: ChangeDetectorRef,
    private _ladataanService: LadataanService,
    private _formValidationService: FormValidationService,
    private _currencyService: CurrencyService,
    private _dateService: DateService,
    private _firebase: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _dialog: MatDialog,
    private _asiakasUriService: AsiakasUriService,
  ) {
    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

    const kuukausiruutuUri = this._getKuukausiruutuUri(this._data)
    this.kuukausiruutuUriObservable = of(kuukausiruutuUri)
    this.kuukausiruutuEncodedUriObservable = this.kuukausiruutuUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )

    const localMonth = this._dateService.numberToLocalMonth(this._data.kuukausi.k)
    this.lokalisoituKuukausiVuosi = _dateService.annaKuukaudenNimiPaikallinen(localMonth, 'fi') + ' ' + localMonth.year

    this.asiakas = this._data.asiakas
    this.kuukausi = this._data.kuukausi

    this.onkoTmi = this.asiakas.c === Yritysmuoto.TOIMINIMI
    this.onkoTilikaudenLoppu = !!this.kuukausi.l

    // Onko murrettu tilikausi
    this.onkoMurrettuTilikausi = !!this.kuukausi.bmt

    if (this.kuukausi.h || this.kuukausi.h === 0) {
      this.hinta = this.kuukausi.h
    }

    this.naytaTilinpaatos = this.onkoTilikaudenLoppu && (!this.onkoTmi || this.onkoMurrettuTilikausi)
    this.naytaVeroilmoitus = this.onkoTilikaudenLoppu
    this.naytaPoytakirja = this.onkoTilikaudenLoppu && !this.onkoTmi

    if (this.hinta && this.kuukausi.ka) {
      this.teho = this._currencyService.formatoiDesimaali((this.hinta / this.kuukausi.ka) * 60, 2, 'fi')
    }

    const kirjanopitoTehty = !!this.kuukausi.kp
    const tilinpaatosTehty = !!this.kuukausi.tp
    const poytakirjaTehty = !!this.kuukausi.pp
    const veroilmoitusTehty = !!this.kuukausi.vp
    const aktiivinen = !this.kuukausi.bd
    const alvOverride = !!this.kuukausi.baok

    const initialKirjanpitoAika = this.kuukausi.ka ? Math.round(this.kuukausi.ka) : null

    this._poissa = {
      alvIlmoitusHoidettuJarjestelmanUlkopuolella: signal(!aktiivinen),
      tiedotOsinkojenMaksamisesta: signal(!aktiivinen),
      kirjanpitoTehty: signal(!aktiivinen),
      kirjanpitoPvm: signal(!kirjanopitoTehty),
      tilinpaatosTehty: signal(!aktiivinen),
      tilinpaatosPvm: signal(!tilinpaatosTehty),
      veroilmoitusTehty: signal(!aktiivinen),
      veroilmoitusPvm: signal(!veroilmoitusTehty),
      poytakirjaTehty: signal(!aktiivinen),
      poytakirjaPvm: signal(!poytakirjaTehty),
    }
    // Näkyvä päivämääräkenttä on aluksi pakollinen, ja tehty-valinnan muutos asettaa pakollisuuden uudelleen
    this._pakollinen.kirjanpitoPvm.set(true)
    this._pakollinen.tilinpaatosPvm.set(this.naytaTilinpaatos || tilinpaatosTehty)
    this._pakollinen.veroilmoitusPvm.set(this.naytaVeroilmoitus || veroilmoitusTehty)
    this._pakollinen.poytakirjaPvm.set(this.naytaPoytakirja || poytakirjaTehty)

    const alkuarvo: KirjanpitostatusLomake = {
      aktiivinen,
      alvIlmoitusHoidettuJarjestelmanUlkopuolella: alvOverride,
      tiedotOsinkojenMaksamisesta: this.kuukausi.tom ?? null,
      kirjanpitoTehty: kirjanopitoTehty,
      kirjanpitoPvm: this._dateService.numberToDate(this.kuukausi.kp),
      kirjanpitoAika: initialKirjanpitoAika === null ? '' : '' + initialKirjanpitoAika,
      tilinpaatosTehty,
      tilinpaatosPvm: this._dateService.numberToDate(this.kuukausi.tp),
      veroilmoitusTehty,
      veroilmoitusPvm: this._dateService.numberToDate(this.kuukausi.vp),
      poytakirjaTehty,
      poytakirjaPvm: this._dateService.numberToDate(this.kuukausi.pp),
      viesti: this.kuukausi.m ?? '',
    }
    this.lomake = form(signal(alkuarvo), polku => {
      disabled(polku.alvIlmoitusHoidettuJarjestelmanUlkopuolella, () => this._poissa.alvIlmoitusHoidettuJarjestelmanUlkopuolella())
      disabled(polku.tiedotOsinkojenMaksamisesta, () => this._poissa.tiedotOsinkojenMaksamisesta())
      disabled(polku.kirjanpitoTehty, () => this._poissa.kirjanpitoTehty())
      disabled(polku.kirjanpitoPvm, () => this._poissa.kirjanpitoPvm())
      disabled(polku.tilinpaatosTehty, () => this._poissa.tilinpaatosTehty())
      disabled(polku.tilinpaatosPvm, () => this._poissa.tilinpaatosPvm())
      disabled(polku.veroilmoitusTehty, () => this._poissa.veroilmoitusTehty())
      disabled(polku.veroilmoitusPvm, () => this._poissa.veroilmoitusPvm())
      disabled(polku.poytakirjaTehty, () => this._poissa.poytakirjaTehty())
      disabled(polku.poytakirjaPvm, () => this._poissa.poytakirjaPvm())

      required(polku.kirjanpitoAika)
      validoiReaktiivisesti(polku.kirjanpitoAika, Validators.min(this.kirjanpitoAikaVahintaan))
      validoiReaktiivisesti(polku.kirjanpitoAika, Validators.max(this.kirjanpitoAikaEnintaan))
      validoiReaktiivisesti(polku.kirjanpitoAika, this.annaVainNumerotValidator())

      // Tähti näytetään aina, vaikka pakollisuus vaihtelee tehty-valinnan mukaan
      required(polku.kirjanpitoPvm, { when: () => this._pakollinen.kirjanpitoPvm() })
      metadata(polku.kirjanpitoPvm, REQUIRED, () => true)
      required(polku.tilinpaatosPvm, { when: () => this._pakollinen.tilinpaatosPvm() })
      metadata(polku.tilinpaatosPvm, REQUIRED, () => true)
      required(polku.veroilmoitusPvm, { when: () => this._pakollinen.veroilmoitusPvm() })
      metadata(polku.veroilmoitusPvm, REQUIRED, () => true)
      required(polku.poytakirjaPvm, { when: () => this._pakollinen.poytakirjaPvm() })
      metadata(polku.poytakirjaPvm, REQUIRED, () => true)
    })
  }

  private _asetaPoissa(nimi: PoistettavaKentta, poissa: boolean) {
    this._poissa[nimi].set(poissa)
    // Käytöstilan vaihto lähettää kentän arvon tilaajille, jolloin esim. tehty-valinnan käsittelijä ajetaan uudelleen
    const kentta = this.lomake[nimi] as FieldTree<unknown>
    asetaArvo(kentta, kentta().value())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string) {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private annaVainNumerotValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value && typeof control.value === 'string' && !this._currencyService.onkoMerkkijonoKokonaisluku(control.value.trim())) {
        return { number: true }
      }
      return null
    }
  }

  ngOnInit() {
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.lomake.alvIlmoitusHoidettuJarjestelmanUlkopuolella)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value) {
          this.kuukausi.baok = 1
        } else {
          delete this.kuukausi.baok
        }
      })
    muutokset(this.lomake.tiedotOsinkojenMaksamisesta)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value) {
          this.kuukausi.tom = value
        } else {
          delete this.kuukausi.tom
        }
      })

    muutokset(this.lomake.kirjanpitoTehty)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.muutaOnkoKirjanpitoriviEstetty(!!value, false, true)
      })
    muutokset(this.lomake.kirjanpitoPvm)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.kuukausi.kp = this._dateService.dateToNumber(value)
        if (!this.kuukausi.kp) {
          delete this.kuukausi.kp
        }
      })
    muutokset(this.lomake.kirjanpitoAika)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.kuukausi.ka = this._currencyService.muutaMerkkijonoNumeroksi(value, 0)
        if (!this.kuukausi.ka) {
          delete this.kuukausi.ka
        }
        if (this.hinta && this.kuukausi.ka) {
          this.teho = this._currencyService.formatoiDesimaali((this.hinta / this.kuukausi.ka) * 60, 2, 'fi')
        }
      })

    muutokset(this.lomake.tilinpaatosTehty)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.muutaOnkoTilinpaatosriviEstetty(!!value, false, true)
      })
    muutokset(this.lomake.tilinpaatosPvm)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.kuukausi.tp = this._dateService.dateToNumber(value)
        if (!this.kuukausi.tp) {
          delete this.kuukausi.tp
        }
      })
    // this.form.get('tilinpaatosAika').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
    //   this.kuukausi.ta = this._currencyService.muutaMerkkijonoNumeroksi(value, 0)
    //   if (!this.kuukausi.ta) {
    //     delete this.kuukausi.ta
    //   }
    // })

    muutokset(this.lomake.veroilmoitusTehty)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.muutaOnkoVeroilmoitusriviEstetty(!!value, false, true)
      })
    muutokset(this.lomake.veroilmoitusPvm)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.kuukausi.vp = this._dateService.dateToNumber(value)
        if (!this.kuukausi.vp) {
          delete this.kuukausi.vp
        }
      })
    // this.form.get('veroilmoitusAika').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
    //   this.kuukausi.va = this._currencyService.muutaMerkkijonoNumeroksi(value, 0)
    //   if (!this.kuukausi.va) {
    //     delete this.kuukausi.va
    //   }
    // })

    muutokset(this.lomake.poytakirjaTehty)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.kuukausi.tpp = KirjanpitoKuukausiruudunTilinpaatoksenTila.VALMIS
        this.muutaOnkoPoytakirjariviEstetty(!!value, false, true)
      })
    muutokset(this.lomake.poytakirjaPvm)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.kuukausi.pp = this._dateService.dateToNumber(value)
        if (!this.kuukausi.pp) {
          delete this.kuukausi.pp
        }
      })
    // this.form.get('poytakirjaAika').valueChanges.subscribe(value => {
    //   this.kuukausi.poytakirjaAika = this._currencyService.muutaMerkkijonoNumeroksi(value, 0)
    // })

    muutokset(this.lomake.aktiivinen)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (!value) {
          this.kuukausi.bd = 1
        } else {
          delete this.kuukausi.bd
        }

        if (value) {
          this._asetaPoissa('kirjanpitoTehty', false)
          if (this.naytaTilinpaatos) {
            this._asetaPoissa('tilinpaatosTehty', false)
          }
          if (this.naytaVeroilmoitus) {
            this._asetaPoissa('veroilmoitusTehty', false)
          }
          if (this.naytaPoytakirja) {
            this._asetaPoissa('poytakirjaTehty', false)
          }
          this._asetaPoissa('alvIlmoitusHoidettuJarjestelmanUlkopuolella', false)
          this._asetaPoissa('tiedotOsinkojenMaksamisesta', false)
        } else {
          this._asetaPoissa('alvIlmoitusHoidettuJarjestelmanUlkopuolella', true)
          this._asetaPoissa('tiedotOsinkojenMaksamisesta', true)
          this._asetaPoissa('kirjanpitoTehty', true)
          if (this.naytaTilinpaatos) {
            this._asetaPoissa('tilinpaatosTehty', true)
          }
          if (this.naytaVeroilmoitus) {
            this._asetaPoissa('veroilmoitusTehty', true)
          }
          if (this.naytaPoytakirja) {
            this._asetaPoissa('poytakirjaTehty', true)
          }
        }
        this.muutaOnkoKirjanpitoriviEstetty(value && this.lomake.kirjanpitoTehty().value(), true, false)
        if (this.naytaTilinpaatos) {
          this.muutaOnkoTilinpaatosriviEstetty(value && this.lomake.tilinpaatosTehty().value(), true, false)
        }
        if (this.naytaVeroilmoitus) {
          this.muutaOnkoVeroilmoitusriviEstetty(value && this.lomake.veroilmoitusTehty().value(), true, false)
        }
        if (this.naytaPoytakirja) {
          this.muutaOnkoPoytakirjariviEstetty(value && this.lomake.poytakirjaTehty().value(), true, false)
        }

        this.asetaFocusLomakkeelle()
      })

    muutokset(this.lomake.viesti)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value && value.trim()) {
          this.kuukausi.m = value
        } else {
          delete this.kuukausi.m
        }
      })
  }

  ngAfterViewInit() {
    this.asetaFocusLomakkeelle()
  }

  private asetaFocusLomakkeelle() {
    setTimeout(() => {
      if (!this.kuukausi.d) {
        if (this.kirjanpitoTehtyCheckbox && (!this.kuukausi || !this.kuukausi.kp)) {
          this.kirjanpitoTehtyCheckbox.focus()
        } else if (this.tilinpaatosTehtyCheckbox && this.kuukausi && this.naytaTilinpaatos && !this.kuukausi.tp) {
          this.tilinpaatosTehtyCheckbox.focus()
        } else if (this.veroilmoitusTehtyCheckbox && this.kuukausi && this.naytaVeroilmoitus && !this.kuukausi.vp) {
          this.veroilmoitusTehtyCheckbox.focus()
        } else if (this.poytakirjaTehtyCheckbox && this.kuukausi && this.naytaPoytakirja && !this.kuukausi.pp) {
          this.poytakirjaTehtyCheckbox.focus()
        } else {
          this.muistiinpanotTextarea.focus()
          // this.kirjanpitoaikaInput.focus()
        }
      }
      this._changeDetectorRef.markForCheck()
    }, 10)
  }

  private muutaOnkoTilinpaatosriviEstetty(value: boolean, alaAsetaFokusta: boolean, clearField: boolean) {
    if (value) {
      this._asetaPoissa('tilinpaatosPvm', false)
      // this.form.get('tilinpaatosAika').enable()
      this._pakollinen.tilinpaatosPvm.set(true)
      // this.form.get('tilinpaatosAika').setValidators([Validators.required, Validators.min(1), Validators.max(1440), this.annaVainNumerotValidator()])
      if (
        !this.kuukausi.tp &&
        // this.tilinpaatosaikaInput &&
        !alaAsetaFokusta
      ) {
        asetaArvo(this.lomake.tilinpaatosPvm, new Date())
        // setTimeout(() => {
        //   this.tilinpaatosaikaInput.focus()
        //   this._changeDetectorRef.markForCheck()
        // }, 10)
      }
    } else {
      this._pakollinen.tilinpaatosPvm.set(false)
      // this.form.get('tilinpaatosAika').clearValidators()
      if (clearField) {
        asetaArvo(this.lomake.tilinpaatosPvm, null)
        // this.form.get('tilinpaatosAika').setValue(null)
      }
      this._asetaPoissa('tilinpaatosPvm', true)
      // this.form.get('tilinpaatosAika').disable()
    }
  }

  private muutaOnkoKirjanpitoriviEstetty(value: boolean, alaAsetaFokusta: boolean, clearField: boolean) {
    if (value) {
      this._pakollinen.kirjanpitoPvm.set(true)
      // this.form.get('kirjanpitoAika').setValidators([Validators.required, Validators.min(1), Validators.max(1440), this.annaVainNumerotValidator()])
      this._asetaPoissa('kirjanpitoPvm', false)
      // this.form.get('kirjanpitoAika').enable()
      if (
        !this.kuukausi.kp &&
        // this.kirjanpitoaikaInput &&
        !alaAsetaFokusta
      ) {
        asetaArvo(this.lomake.kirjanpitoPvm, new Date())
        // setTimeout(() => {
        //   this.kirjanpitoaikaInput.focus()
        //   this._changeDetectorRef.markForCheck()
        // }, 10)
      }
    } else {
      this._pakollinen.kirjanpitoPvm.set(false)
      // this.form.get('kirjanpitoAika').clearValidators()
      if (clearField) {
        asetaArvo(this.lomake.kirjanpitoPvm, null)
        // this.form.get('kirjanpitoAika').setValue(null)
      }
      this._asetaPoissa('kirjanpitoPvm', true)
      // this.form.get('kirjanpitoAika').disable()
    }
  }

  private muutaOnkoVeroilmoitusriviEstetty(value: boolean, alaAsetaFokusta: boolean, clearField: boolean) {
    if (value) {
      this._asetaPoissa('veroilmoitusPvm', false)
      // this.form.get('veroilmoitusAika').enable()
      this._pakollinen.veroilmoitusPvm.set(true)
      // this.form.get('veroilmoitusAika').setValidators([Validators.required, Validators.min(1), Validators.max(1440), this.annaVainNumerotValidator()])
      if (
        !this.kuukausi.vp &&
        // this.veroilmoitusaikaInput &&
        !alaAsetaFokusta
      ) {
        asetaArvo(this.lomake.veroilmoitusPvm, new Date())
        // setTimeout(() => {
        //   this.veroilmoitusaikaInput.focus()
        //   this._changeDetectorRef.markForCheck()
        // }, 10)
      }
    } else {
      this._pakollinen.veroilmoitusPvm.set(false)
      // this.form.get('veroilmoitusAika').clearValidators()
      if (clearField) {
        asetaArvo(this.lomake.veroilmoitusPvm, null)
        // this.form.get('veroilmoitusAika').setValue(null)
      }
      this._asetaPoissa('veroilmoitusPvm', true)
      // this.form.get('veroilmoitusAika').disable()
    }
  }

  private muutaOnkoPoytakirjariviEstetty(value: boolean, alaAsetaFokusta: boolean, clearField: boolean) {
    if (value) {
      this._asetaPoissa('poytakirjaPvm', false)
      // this.form.get('poytakirjaAika').enable()
      this._pakollinen.poytakirjaPvm.set(true)
      // this.form.get('poytakirjaAika').setValidators([ Validators.min(1), Validators.required, Validators.max(1440), this.annaVainNumerotValidator() ])
      if (!this.kuukausi.pp && this.poytakirjaPvmInput && !alaAsetaFokusta) {
        asetaArvo(this.lomake.poytakirjaPvm, new Date())
        setTimeout(() => {
          this.poytakirjaPvmInput.focus()
          this._changeDetectorRef.markForCheck()
        }, 10)
      }
    } else {
      this._pakollinen.poytakirjaPvm.set(false)
      // this.form.get('poytakirjaAika').clearValidators()
      if (clearField) {
        asetaArvo(this.lomake.poytakirjaPvm, null)
      }
      // this.form.get('poytakirjaAika').setValue(null)
      this._asetaPoissa('poytakirjaPvm', true)
      // this.form.get('poytakirjaAika').disable()
    }
  }

  // get tilinpaatosAika(): AbstractControl {
  //   return this.form.get('tilinpaatosAika')
  // }

  // get veroilmoitusAika(): AbstractControl {
  //   return this.form.get('veroilmoitusAika')
  // }

  async tallenna() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    const vahvistaRekisterointi = await this._vahvistaTilinpaatoksenRekisterointi()
    if (!vahvistaRekisterointi) {
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      // Remove "waits for dividends to be registered", if accountant marks as registered.
      if (this.kuukausi.tom) {
        delete this.kuukausi.oor
      }

      const data: AsiakkaanKuukausiruutuStatusSaveRequest = {
        asiakasAvain: this._data.asiakas.k,
        ruutu: this.kuukausi,
      }

      return this._firebase.functionsCall<AsiakkaanKuukausiruutuStatusSaveRequest, AsiakkaanKuukausiruutuStatusSaveResponse>('kuukausiruutuPaivitaStatus', data).then(resp => {
        this._ladataanService.lopetaLataaminen()
        if (!resp || resp.e) {
          this._errorHandler.handleError(new Error(resp ? resp.e : 'unknown-error'))
          this._snackbar.open('Tallennus epäonnistui, yritä uudelleen', 'OK', { duration: 5000 })
        } else {
          this._dialogRef.close()
        }
      })
    } catch (err) {
      this._ladataanService.lopetaLataaminen()
      this._errorHandler.handleError(err)
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  private async _vahvistaTilinpaatoksenRekisterointi() {
    const tilinpaatosMerkittiinNytRekisteroidyksi = this.poytakirjaTehtyMuokattu && !!this.lomake.poytakirjaTehty().value()
    if (!tilinpaatosMerkittiinNytRekisteroidyksi) {
      return true
    }

    const dialogData: AreYouSureDialogData = {
      rightAction: 'Tallenna',
      leftAction: 'Peruuta',
      text: 'Oletko varma, että haluat merkitä tilinpäätöksen rekisteröidyksi?',
    }
    const vahvistettu = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed())
    return !!vahvistettu
  }
}
